import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { FiPlus, FiTrash, FiEdit, FiCopy } from 'react-icons/fi';
import api, { feeStructuresService, feeCategoriesService } from '../../services/api';
import { formatFcfa } from '../../utils/feeUtils';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import ConditionalButton from '../../components/common/ConditionalButton';
import { usePermissions } from '../../hooks/usePermissions';

const emptyInstallment = () => ({ label: '', due_date: '', amount: '', sort_order: 1 });

const getOuvertureId = (oc) => String(oc.ouverture_classe_id ?? oc.id ?? '');
const getClasseLabel = (oc) => oc.classe?.classe || oc.classe_nom || `Classe #${getOuvertureId(oc)}`;

const FeeStructuresPage = () => {
  const { canUpdate, canDelete } = usePermissions();
  const { selectedYear } = useAcademicYear();
  const [structures, setStructures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ouvertures, setOuvertures] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    fee_category_id: '', ouverture_classe_id: '', installments: [emptyInstallment()],
  });
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    if (!selectedYear?.id) return;
    const params = { annee_scolaire_id: selectedYear.id };
    const [structRes, catRes, ocRes] = await Promise.all([
      feeStructuresService.getAll(params),
      feeCategoriesService.getAll(),
      api.get('/ouvertures-classes', { params }),
    ]);
    setStructures(structRes.data.data || []);
    setCategories(catRes.data.data || []);
    setOuvertures(ocRes.data.data || []);
  };

  useEffect(() => { fetchData(); }, [selectedYear]);

  // Réinitialiser la classe sélectionnée si l'année change
  useEffect(() => {
    setForm((prev) => ({ ...prev, ouverture_classe_id: '' }));
  }, [selectedYear?.id]);

  const resetForm = () => {
    setForm({ fee_category_id: '', ouverture_classe_id: '', installments: [emptyInstallment()] });
    setEditingId(null);
    setShowForm(false);
  };

  const addInstallment = () => {
    setForm({
      ...form,
      installments: [...form.installments, { ...emptyInstallment(), sort_order: form.installments.length + 1 }],
    });
  };

  const removeInstallment = (idx) => {
    if (form.installments.length <= 1) return;
    setForm({ ...form, installments: form.installments.filter((_, i) => i !== idx) });
  };

  const copyInstallment = (idx) => {
    const source = form.installments[idx];
    const copy = {
      label: source.label,
      due_date: source.due_date,
      amount: source.amount,
      sort_order: form.installments.length + 1,
    };
    const installments = [...form.installments];
    installments.splice(idx + 1, 0, copy);
    setForm({ ...form, installments });
  };

  const updateInstallment = (idx, field, value) => {
    const installments = [...form.installments];
    installments[idx] = { ...installments[idx], [field]: value };
    setForm({ ...form, installments });
  };

  const totalAmount = form.installments.reduce((sum, i) => sum + (parseInt(i.amount) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const feeCategoryId = Number(form.fee_category_id);
    const ouvertureClasseId = Number(form.ouverture_classe_id);

    if (!feeCategoryId || !ouvertureClasseId) {
      Swal.fire({ icon: 'warning', title: 'Champs requis', text: 'Veuillez sélectionner une rubrique et une classe.' });
      return;
    }

    const invalidInstallment = form.installments.some((inst) => !inst.due_date || !inst.amount);
    if (invalidInstallment) {
      Swal.fire({ icon: 'warning', title: 'Échéances incomplètes', text: 'Chaque tranche doit avoir une date et un montant.' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        fee_category_id: feeCategoryId,
        ouverture_classe_id: ouvertureClasseId,
        installments: form.installments.map((inst, idx) => ({
          label: inst.label || `Tranche ${idx + 1}`,
          due_date: inst.due_date,
          amount: parseInt(inst.amount, 10),
          sort_order: idx + 1,
        })),
      };
      if (editingId) {
        await feeStructuresService.update(editingId, payload);
        Swal.fire({ icon: 'success', title: 'Grille mise à jour', timer: 2000, showConfirmButton: false });
      } else {
        await feeStructuresService.create(payload);
        Swal.fire({ icon: 'success', title: 'Grille créée', timer: 2000, showConfirmButton: false });
      }
      resetForm();
      fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: err.response?.data?.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (structure) => {
    setEditingId(structure.id);
    setForm({
      fee_category_id: String(structure.fee_category_id),
      ouverture_classe_id: String(structure.ouverture_classe_id),
      installments: structure.installments?.length
        ? structure.installments.map((i) => ({
            label: i.label, due_date: i.due_date, amount: i.amount, sort_order: i.sort_order,
          }))
        : [emptyInstallment()],
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette grille ?', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Supprimer',
    });
    if (!result.isConfirmed) return;
    await feeStructuresService.delete(id);
    fetchData();
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Grilles tarifaires</h1>
        <ConditionalButton entity="frais-scolarite" permission="create"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700">
          <FiPlus /><span>Nouvelle grille</span>
        </ConditionalButton>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-medium mb-4">{editingId ? 'Modifier la grille' : 'Nouvelle grille tarifaire'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Rubrique</label>
                <select required value={String(form.fee_category_id)} disabled={!!editingId}
                  onChange={(e) => setForm({ ...form, fee_category_id: e.target.value })}
                  className="mt-1 block w-full border rounded-md p-2">
                  <option value="">Sélectionner...</option>
                  {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Classe {selectedYear && <span className="text-gray-400 font-normal">({selectedYear.annee_scolaire})</span>}
                </label>
                <select required value={form.ouverture_classe_id} disabled={!!editingId}
                  onChange={(e) => setForm({ ...form, ouverture_classe_id: e.target.value })}
                  className="mt-1 block w-full border rounded-md p-2">
                  <option value="">Sélectionner...</option>
                  {ouvertures.map((oc) => (
                    <option key={getOuvertureId(oc)} value={getOuvertureId(oc)}>
                      {getClasseLabel(oc)}
                    </option>
                  ))}
                </select>
                {ouvertures.length === 0 && (
                  <p className="text-sm text-orange-600 mt-1">Aucune classe ouverte pour cette année scolaire.</p>
                )}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">Échéances (tranches)</label>
                <button type="button" onClick={addInstallment}
                  className="text-sm text-primary-600 hover:text-primary-800">+ Ajouter une tranche</button>
              </div>
              {form.installments.map((inst, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-4">
                    <input type="text" placeholder="Libellé" value={inst.label}
                      onChange={(e) => updateInstallment(idx, 'label', e.target.value)}
                      className="w-full border rounded-md p-2 text-sm" />
                  </div>
                  <div className="col-span-3">
                    <input type="date" required value={inst.due_date}
                      onChange={(e) => updateInstallment(idx, 'due_date', e.target.value)}
                      className="w-full border rounded-md p-2 text-sm" />
                  </div>
                  <div className="col-span-3">
                    <input type="number" required min="0" placeholder="Montant FCFA" value={inst.amount}
                      onChange={(e) => updateInstallment(idx, 'amount', e.target.value)}
                      className="w-full border rounded-md p-2 text-sm" />
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    <button type="button" onClick={() => copyInstallment(idx)}
                      title="Copier cette tranche"
                      className="text-primary-600 hover:text-primary-800 p-2">
                      <FiCopy />
                    </button>
                    {form.installments.length > 1 && (
                      <button type="button" onClick={() => removeInstallment(idx)}
                        title="Supprimer cette tranche"
                        className="text-red-500 hover:text-red-700 p-2">
                        <FiTrash />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <p className="text-sm text-gray-600 mt-2">Total : <strong>{formatFcfa(totalAmount)}</strong></p>
            </div>

            <div className="flex space-x-2">
              <ConditionalButton entity="frais-scolarite" permission="create" type="submit" disabled={loading}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700">
                {editingId ? 'Mettre à jour' : 'Créer'}
              </ConditionalButton>
              <button type="button" onClick={resetForm}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">Annuler</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Classe</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rubrique</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant total</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tranches</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {structures.map((s) => (
              <tr key={s.id}>
                <td className="px-4 py-3 text-sm">{s.classe}</td>
                <td className="px-4 py-3 text-sm font-medium">{s.category_name}</td>
                <td className="px-4 py-3 text-sm">{formatFcfa(s.total_amount)}</td>
                <td className="px-4 py-3 text-sm">
                  {s.installments?.map((i) => (
                    <div key={i.id} className="text-xs text-gray-600">
                      {i.label} — {formatFcfa(i.amount)} — {new Date(i.due_date).toLocaleDateString('fr-FR')}
                    </div>
                  ))}
                </td>
                <td className="px-4 py-3 text-right">
                  {canUpdate('frais-scolarite') && (
                    <button onClick={() => handleEdit(s)} className="text-primary-600 mr-3"><FiEdit /></button>
                  )}
                  {canDelete('frais-scolarite') && (
                    <button onClick={() => handleDelete(s.id)} className="text-red-600"><FiTrash /></button>
                  )}
                </td>
              </tr>
            ))}
            {structures.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">Aucune grille tarifaire configurée</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FeeStructuresPage;
